/* Commerce adapter for the approved build 41 renderer.
 * Renderer geometry, lighting, materials and rotation remain unmodified.
 * Shopify supplies URLs, variants and market-formatted prices in JSON.
 */
(() => {
  'use strict';
  if (window.HansStudioViewer) {
    window.HansStudioViewer.mountAll(document);
    return;
  }
  const colors = ['black', 'white', 'graphite', 'yellow'];
  const labels = {
    black: 'Mogul Mirrors', white: 'Blizzard Blinders',
    graphite: 'Mountain Monocles', yellow: 'Snow Bunny Scopes'
  };
  const instances = new Map();
  let rendererPromise;
  const title = value => value[0].toUpperCase() + value.slice(1);
  const save = color => { try { sessionStorage.setItem('hansProposalColor', color); } catch {} };
  const saved = () => { try { return sessionStorage.getItem('hansProposalColor'); } catch { return null; } };

  function readConfig(el) {
    let local = {}, commerce = {};
    const commerceScript = document.getElementById('hans-commerce-config');
    if (commerceScript) {
      try { commerce = JSON.parse(commerceScript.textContent); } catch {}
    }
    const script = el.querySelector('script[data-viewer-config]');
    if (script) {
      try { local = JSON.parse(script.textContent); } catch (error) {
        console.warn('Hans viewer configuration could not be read.', error);
      }
    }
    const shared = { ...commerce, ...(window.HansViewerConfig || {}) };
    return {
      ...shared, ...local,
      rendererUrl: el.dataset.rendererUrl || local.rendererUrl || shared.rendererUrl,
      productUrl: el.dataset.productUrl || local.productUrl || shared.productUrl,
      variants: { ...(shared.variants || {}), ...(local.variants || {}) }
    };
  }

  function loadRenderer(url) {
    if (window.hansStudioPair) return Promise.resolve();
    if (!rendererPromise) {
      rendererPromise = new Promise((resolve, reject) => {
        if (!url) { reject(new Error('Missing renderer URL.')); return; }
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => window.hansStudioPair
          ? resolve() : reject(new Error('Renderer did not initialize.'));
        script.onerror = () => { script.remove(); reject(new Error('Renderer could not load.')); };
        document.head.append(script);
      }).catch(error => { rendererPromise = undefined; throw error; });
    }
    return rendererPromise;
  }

  function mount(el) {
    if (instances.has(el)) return;
    const stage = el.querySelector('.stage');
    const poster = stage?.querySelector('img');
    const canvas = stage?.querySelector('canvas');
    if (!stage || !poster || !canvas) return;
    const config = readConfig(el);
    const controller = new AbortController();
    const signal = controller.signal;
    // The renderer uses this separate controller to dispose its WebGL resources.
    el.controller = new AbortController();
    let color = 'black';
    let shields = el.dataset.shieldChoice === 'off' ? 'off' : 'on';
    let disposed = false, initializing = false, initialized = false, dragStart = null;
    let watch;
    const listen = (target, type, handler) => target.addEventListener(type, handler, { signal });
    const variant = () => {
      const pair = config.variants[color] || {};
      const mirror = pair.mirror
        ? (pair.mirror.toLowerCase().endsWith('mirror') ? pair.mirror : pair.mirror + ' ' + (config.strings?.mirror || 'mirror'))
        : (color === 'black' ? 'Bronze mirror' : 'Silver mirror');
      return {
        ...pair,
        label: pair.label || pair.name || labels[color],
        finish: pair.finish || mirror
      };
    };

    function sync() {
      const pair = variant();
      el.dataset.choice = color;
      el.dataset.shieldChoice = shields;
      const file = el.dataset[color + (shields === 'on' ? 'On' : 'Off')];
      if (file && poster.getAttribute('src') !== file) poster.src = file;
      poster.alt = title(color) + ' Legacy Lenses, shields ' + shields;
      el.querySelectorAll('[data-color]').forEach(button =>
        button.setAttribute('aria-pressed', String(button.dataset.color === color)));
      el.querySelectorAll('[data-shields]').forEach(button =>
        button.setAttribute('aria-pressed', String(button.dataset.shields === shields)));
      const name = el.querySelector('.d-optics__name');
      const finish = el.querySelector('.d-optics__finish');
      const price = el.querySelector('.d-optics__price');
      if (name) name.textContent = pair.label;
      if (finish) finish.textContent = pair.finish;
      if (price && pair.formattedPrice) price.textContent = pair.formattedPrice;
      const add = el.querySelector('.d-optics__add');
      if (add && add.getAttribute('aria-busy') !== 'true') {
        const unavailable = !pair.id || pair.available === false;
        add.disabled = unavailable;
        add.setAttribute('aria-disabled', String(unavailable));
        add.textContent = unavailable ? (config.soldOutLabel || config.strings?.sold_out || 'Sold out') : (config.addLabel || config.strings?.add || 'Add to bag');
      }
      el.querySelectorAll('[data-details]').forEach(link => {
        if (!config.productUrl) return;
        const url = new URL(config.productUrl, location.origin);
        if (pair.id) url.searchParams.set('variant', pair.id);
        link.href = url.pathname + url.search + url.hash;
      });
      el.dispatchEvent(new CustomEvent('hans:viewer:change', {
        bubbles: true, detail: { color, shields, ...pair }
      }));
    }

    function applyColor(next, updateUrl = false) {
      if (!colors.includes(next)) return;
      color = next;
      save(color);
      sync();
      if (el.dataset.ready === 'true' && el.api) el.api.setColor(color);
      if (updateUrl && config.updateUrl === true && variant().id) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant().id);
        try { history.replaceState(history.state, '', url); } catch {}
      }
    }

    function restore() {
      const id = new URL(location.href).searchParams.get('variant');
      const matched = id && colors.find(key => String(config.variants[key]?.id) === id);
      const initialId = typeof config.initialVariant === 'object' ? config.initialVariant?.id : config.initialVariant;
      const initial = initialId && colors.find(key => String(config.variants[key]?.id) === String(initialId));
      const fallback = config.defaultColor || el.dataset.choice || 'black';
      applyColor(matched || initial || (colors.includes(saved()) ? saved() : fallback));
    }

    function readyControls() {
      const ready = el.dataset.ready === 'true';
      stage.tabIndex = ready ? 0 : -1;
      stage.setAttribute('aria-disabled', String(!ready));
    }

    function showFallback() {
      el.dataset.ready = 'false';
      canvas.hidden = true;
      poster.hidden = false;
      const status = el.querySelector('.status');
      if (status) status.textContent = config.fallbackLabel || 'Showing studio photos. Color and shield options are available.';
      readyControls();
    }

    const observer = new MutationObserver(readyControls);
    observer.observe(el, { attributes: true, attributeFilter: ['data-ready'] });
    restore();
    readyControls();
    listen(window, 'popstate', restore);
    listen(document, 'hans:color', event => applyColor(event.detail?.color));
    listen(stage, 'pointerdown', event => { dragStart = event.clientX; });
    listen(stage, 'pointermove', event => {
      if (dragStart !== null && Math.abs(event.clientX - dragStart) > 7) el.dataset.turned = 'true';
    });
    listen(window, 'pointerup', () => { dragStart = null; });
    listen(stage, 'pointercancel', () => { dragStart = null; });
    listen(stage, 'keydown', event => {
      if (['ArrowLeft', 'ArrowRight'].includes(event.key)) el.dataset.turned = 'true';
    });
    listen(el, 'click', event => {
      const button = event.target.closest('button');
      if (!button || button.disabled) return;
      if (colors.includes(button.dataset.color)) {
        applyColor(button.dataset.color, true);
        document.dispatchEvent(new CustomEvent('hans:color', { detail: { color } }));
      }
      if (['on', 'off'].includes(button.dataset.shields)) {
        shields = button.dataset.shields;
        sync();
        if (el.dataset.ready === 'true' && el.api) el.api.setShields(shields === 'on');
      }
      if (button.matches('.d-optics__add')) {
        const pair = variant();
        if (!pair.id || pair.available === false) return;
        document.dispatchEvent(new CustomEvent('workshop:add', {
          detail: {
            id: String(pair.id), color: title(color), label: pair.label,
            price: pair.formattedPrice || '', source: 'hans-studio-viewer', button
          }
        }));
      }
    });

    async function initialize() {
      if (initializing || initialized || disposed) return;
      initializing = true;
      try {
        if (!window.DecompressionStream || !window.ResizeObserver)
          throw new Error('Photo fallback is required in this browser.');
        await loadRenderer(config.rendererUrl);
        if (disposed || !el.isConnected) return;
        await window.hansStudioPair(el);
        initialized = true;
        if (!disposed) {
          el.api.setColor(color);
          el.api.setShields(shields === 'on');
        }
      } catch (error) {
        if (!disposed) {
          showFallback();
          el.dispatchEvent(new CustomEvent('hans:viewer:error', { bubbles: true, detail: { message: error.message } }));
        }
      } finally {
        initializing = false;
        if (disposed) el.controller.abort();
        else readyControls();
      }
    }

    instances.set(el, () => {
      disposed = true;
      watch?.disconnect();
      observer.disconnect();
      controller.abort();
      if (!initializing) el.controller.abort();
      instances.delete(el);
    });
    if ('IntersectionObserver' in window) {
      watch = new IntersectionObserver(records => {
        if (records.some(record => record.isIntersecting)) { watch.disconnect(); initialize(); }
      }, { rootMargin: '180px' });
      watch.observe(stage);
    } else initialize();
  }

  function mountAll(root) {
    if (root.matches?.('hans-studio-pair')) mount(root);
    root.querySelectorAll('hans-studio-pair').forEach(mount);
  }
  function unmountAll(root) {
    for (const [el, destroy] of instances) if (root === el || root.contains(el)) destroy();
  }
  window.HansStudioViewer = { mountAll, unmountAll };
  document.addEventListener('shopify:section:load', event => mountAll(event.target));
  document.addEventListener('shopify:section:unload', event => unmountAll(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountAll(document), { once: true });
  else mountAll(document);
})();
